"use client"
import { create } from "zustand"

interface FestivalState {
  festivals: any[],
  festival: any,
  festivalsByState: any[],
  error: string | null,
  loading: boolean,

  fetchFestivals: () => Promise<void>,
  fetchFestival: (slug: string) => Promise<void>,
  fetchFestivalsByState: (slug: string) => Promise<void>,
  fetchStateFestivalsFull: (slug: string) => Promise<void>,
}

export const useFestivalSharedData = create<FestivalState>((set) => ({
  festivals: [],
  festival: null,
  festivalsByState: [],
  error: null,
  loading: false,

  //------------------------------------
  // 📌 Fetch ALL festivals
  //------------------------------------
  fetchFestivals: async () => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}festival`)
      if (!res.ok) throw new Error("Failed to fetch festivals")

      const data = await res.json()
      set({ festivals: data.festival.data })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // 📌 Fetch ONE festival
  //------------------------------------
  fetchFestival: async (slug: string) => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}festival/${slug}`)
      if (!res.ok) throw new Error("Failed to fetch festival")

      const data = await res.json()
      set({ festival: data })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // 📌 Fetch Festivals by State
  //------------------------------------
  fetchFestivalsByState: async (slug: string) => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}festivalsbystate/${slug}`
      )
      if (!res.ok) throw new Error("Failed to fetch festivals by state")

      const data = await res.json()
      set({ festivalsByState: data.festivals.data })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // ⭐ Fetch STATE + FESTIVALS
  //   (similar to fetchStateFull)
  //------------------------------------
  fetchStateFestivalsFull: async (slug: string) => {
    set({ loading: true, error: null })

    try {
      const [stateRes, festivalsRes] = await Promise.all([
        fetch(`${process.env.NEXT_PUBLIC_API_URL}states/${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}festivalsbystate/${slug}`),
      ])

      if (!stateRes.ok) throw new Error("Failed to fetch state for festivals")
      if (!festivalsRes.ok) throw new Error("Failed to fetch festivals")

      const stateData = await stateRes.json()
      const festivalsData = await festivalsRes.json()

      set({
        festivalsByState: festivalsData.festivals,
        festival: stateData.state, // optional depending on UI
      })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },
}))
